import React, { useState } from 'react';
import { X, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ImportModal({ onClose, onImported, lines, projects }) {
  const [file, setFile] = useState(null);
  const [lineId, setLineId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const trainProjects = projects.filter(p => p.planning_unit === 'seat');
  const setProjects = projects.filter(p => p.planning_unit === 'set');

  const handleImport = async () => {
    if (!file || !lineId || !projectId) { toast.error('Select file, line, and project'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('project_id', projectId);
      formData.append('line_id', lineId);
      const res = await axios.post(`${API}/api/plans/import-excel`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      toast.success(res.data.message);
      onImported();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="import-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-800">Import from Excel</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        {result ? (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-emerald-800">{result.message}</p>
              {result.date_range && <p className="text-xs text-emerald-600 mt-1">{result.date_range[0]} - {result.date_range[result.date_range.length - 1]}</p>}
            </div>
            <button onClick={onClose} className="w-full px-4 py-2 text-sm bg-slate-800 text-white rounded-lg" data-testid="import-close-btn">Done</button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Excel File (.xlsx)</label>
              <input type="file" accept=".xlsx,.xls" onChange={e => setFile(e.target.files[0])}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="import-file-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Production Line *</label>
              <select value={lineId} onChange={e => setLineId(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="import-line-select">
                <option value="">-- Select Line --</option>
                {lines.map(l => <option key={l.id} value={l.id}>{l.name} ({l.code})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Project *</label>
              <select value={projectId} onChange={e => setProjectId(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="import-project-select">
                <option value="">-- Select Project --</option>
                {trainProjects.length > 0 && <optgroup label="Train (per seat)">
                  {trainProjects.map(p => <option key={p.id} value={p.id}>{p.name} {p.sr_number ? `(${p.sr_number})` : ''}</option>)}
                </optgroup>}
                {setProjects.length > 0 && <optgroup label="Bus / Coach (per set)">
                  {setProjects.map(p => <option key={p.id} value={p.id}>{p.name} {p.sr_number ? `(${p.sr_number})` : ''}</option>)}
                </optgroup>}
              </select>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-[10px] text-blue-700">
              Excel format: Row 1 = headers with date columns. Data rows have P (Planned) and A (Actual) indicators.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={handleImport} disabled={uploading}
                className="px-4 py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-900 disabled:opacity-50" data-testid="import-submit-btn">
                {uploading ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
